import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Receipt, TrendingUp, TrendingDown, Clock, Filter,
  Zap, X, AlertTriangle, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '../api/client';

// ─── Configs ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  OPEN:    { color: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20' },
  PARTIAL: { color: 'text-blue-400',   bg: 'bg-blue-400/10',   border: 'border-blue-400/20'  },
  CLOSED:  { color: 'text-slate-400',  bg: 'bg-slate-400/10',  border: 'border-slate-400/20' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function PnlValue({ value, className = '' }) {
  if (value == null || value === '') return <span className="text-slate-600 font-mono text-xs">—</span>;
  const val = parseFloat(value);
  const color = val > 0 ? 'text-green-400' : val < 0 ? 'text-red-400' : 'text-slate-400';
  return (
    <span className={`font-mono font-bold ${color} ${className}`}>
      {val > 0 ? '+' : ''}{val.toFixed(2)}
    </span>
  );
}

function formatTime(iso, opts = {}) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    ...opts,
  });
}

function elapsed(openTime) {
  if (!openTime) return '—';
  const ms  = Date.now() - new Date(openTime).getTime();
  const min = Math.floor(ms / 60000);
  const hrs = Math.floor(min / 60);
  if (hrs > 0) return `${hrs}h ${min % 60}m`;
  return `${min}m`;
}

// ─── Modal confirmación de cierre ─────────────────────────────────────────────

function ConfirmCloseModal({ position, onConfirm, onCancel, closing }) {
  const isBuy = position.type?.toLowerCase().includes('buy');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-[#13131A] border border-red-400/30 rounded-lg w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-red-400/10 border border-red-400/20 flex items-center justify-center shrink-0">
            <AlertTriangle size={14} className="text-red-400" />
          </div>
          <div>
            <p className="font-mono font-bold text-slate-100 text-sm">Cerrar posición</p>
            <p className="text-[11px] font-mono text-slate-500 mt-0.5">Esta acción se ejecuta en el broker</p>
          </div>
        </div>

        <div className="bg-[#0A0A0F] border border-[#1E1E2E] rounded p-3 space-y-2">
          <div className="flex justify-between text-[11px] font-mono">
            <span className="text-slate-500">Símbolo</span>
            <span className="text-amber-400 font-bold">{position.symbol}</span>
          </div>
          <div className="flex justify-between text-[11px] font-mono">
            <span className="text-slate-500">Dirección</span>
            <span className={isBuy ? 'text-green-400' : 'text-red-400'}>
              {isBuy ? 'COMPRA' : 'VENTA'}
            </span>
          </div>
          <div className="flex justify-between text-[11px] font-mono">
            <span className="text-slate-500">Volumen</span>
            <span className="text-slate-200">{position.volume}</span>
          </div>
          <div className="flex justify-between text-[11px] font-mono">
            <span className="text-slate-500">PnL flotante</span>
            <PnlValue value={position.profit} />
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancel}
            disabled={closing}
            className="flex-1 py-2 rounded text-[11px] font-mono border border-[#2A2A3E] text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={closing}
            className="flex-1 py-2 rounded text-[11px] font-mono bg-red-500 hover:bg-red-400 text-white font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {closing
              ? <><RefreshCw size={11} className="animate-spin" /> Cerrando...</>
              : <><X size={11} /> Confirmar cierre</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Card de posición abierta (tiempo real) ───────────────────────────────────

function LivePositionCard({ position, onClose }) {
  const isBuy   = position.type?.toLowerCase().includes('buy');
  const profit  = parseFloat(position.profit ?? 0);
  const profitColor = profit > 0 ? 'text-green-400' : profit < 0 ? 'text-red-400' : 'text-slate-400';
  const borderColor = profit > 0 ? 'border-green-400/20' : profit < 0 ? 'border-red-400/20' : 'border-[#1E1E2E]';

  return (
    <div className={`bg-[#13131A] border rounded-lg p-4 transition-colors ${borderColor}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
          <span className="font-mono font-bold text-slate-100 text-sm">{position.symbol}</span>
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
            isBuy
              ? 'text-green-400 bg-green-400/10 border-green-400/20'
              : 'text-red-400 bg-red-400/10 border-red-400/20'
          }`}>
            {isBuy ? '▲ BUY' : '▼ SELL'}
          </span>
        </div>
        <button
          onClick={() => onClose(position)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-mono border border-red-400/30 text-red-400 hover:bg-red-400/10 transition-colors"
        >
          <X size={10} /> Cerrar
        </button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <div>
          <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">PnL</p>
          <p className={`font-mono font-bold text-base ${profitColor}`}>
            {profit > 0 ? '+' : ''}{profit.toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">Volumen</p>
          <p className="font-mono text-slate-200 text-sm">{position.volume}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">Entry</p>
          <p className="font-mono text-slate-300 text-xs">{parseFloat(position.openPrice ?? 0).toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">Precio actual</p>
          <p className="font-mono text-slate-300 text-xs">{parseFloat(position.currentPrice ?? 0).toFixed(2)}</p>
        </div>
        {position.stopLoss != null && position.stopLoss !== 0 && (
          <div>
            <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">SL</p>
            <p className="font-mono text-red-400/70 text-xs">{parseFloat(position.stopLoss).toFixed(2)}</p>
          </div>
        )}
        {position.takeProfit != null && position.takeProfit !== 0 && (
          <div>
            <p className="text-[10px] font-mono text-slate-600 uppercase mb-0.5">TP</p>
            <p className="font-mono text-green-400/70 text-xs">{parseFloat(position.takeProfit).toFixed(2)}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-[#1E1E2E] flex items-center justify-between">
        <span className="text-[10px] font-mono text-slate-600">
          <Clock size={9} className="inline mr-1" />
          Abierta hace {elapsed(position.time)}
        </span>
        <span className="text-[10px] font-mono text-slate-600">
          ID: {String(position.id).slice(0, 8)}…
        </span>
      </div>
    </div>
  );
}

// ─── Fila de trade cerrado ────────────────────────────────────────────────────

function TradeRow({ trade }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_CONFIG[trade.status] || STATUS_CONFIG.CLOSED;
  const isBuy     = trade.tradeType?.includes('BUY');

  return (
    <div className={`bg-[#13131A] border rounded transition-all duration-150 ${
      expanded ? 'border-amber-500/20' : 'border-[#1E1E2E]'
    }`}>
      <div
        className="grid grid-cols-12 items-center gap-2 px-4 py-3 cursor-pointer hover:bg-white/[0.02] text-xs font-mono"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Símbolo + tipo */}
        <div className="col-span-2 flex items-center gap-2">
          {isBuy
            ? <TrendingUp  size={12} className="text-green-400 shrink-0" />
            : <TrendingDown size={12} className="text-red-400   shrink-0" />
          }
          <div>
            <p className="text-slate-200 font-bold">{trade.symbol}</p>
            <p className={`text-[10px] ${isBuy ? 'text-green-400' : 'text-red-400'}`}>
              {isBuy ? 'BUY' : 'SELL'}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="col-span-1">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusCfg.color} ${statusCfg.bg} ${statusCfg.border}`}>
            {trade.status}
          </span>
        </div>

        {/* Entry */}
        <div className="col-span-2 text-slate-400">
          <p className="text-[10px] text-slate-600">Entry</p>
          <p>{trade.entryPrice ? parseFloat(trade.entryPrice).toFixed(2) : '—'}</p>
        </div>

        {/* SL / TP */}
        <div className="col-span-2 text-slate-500">
          <p className="text-[10px] text-slate-600">SL / TP</p>
          <p>
            <span className="text-red-400/70">
              {trade.slPrice ? parseFloat(trade.slPrice).toFixed(2) : '—'}
            </span>
            <span className="text-slate-600 mx-1">/</span>
            <span className="text-green-400/70">
              {trade.tpPrice ? parseFloat(trade.tpPrice).toFixed(2) : '—'}
            </span>
          </p>
        </div>

        {/* PnL */}
        <div className="col-span-2">
          <p className="text-[10px] text-slate-600">PnL</p>
          <div className="flex items-center gap-1.5">
            <PnlValue value={trade.totalPnl ?? trade.pnlUsd} />
            {trade.result && (
              <span className={`text-[10px] ${
                trade.result === 'WIN'  ? 'text-green-400' :
                trade.result === 'LOSS' ? 'text-red-400'   : 'text-slate-500'
              }`}>
                {trade.result}
              </span>
            )}
          </div>
        </div>

        {/* Source */}
        <div className="col-span-1">
          <span className={`text-[10px] ${trade.source === 'MANUAL' ? 'text-purple-400' : 'text-slate-600'}`}>
            {trade.source || 'AGENT'}
          </span>
        </div>

        {/* Timestamp + expand */}
        <div className="col-span-2 flex items-center justify-end gap-2 text-slate-600 text-[10px]">
          <span>{formatTime(trade.openedAt)}</span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </div>
      </div>

      {/* Detalle expandido */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#1E1E2E] pt-3 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Volumen</p>
              <p className="text-xs font-mono text-slate-300">{trade.volume || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Duración</p>
              <p className="text-xs font-mono text-slate-300">
                {trade.durationMinutes ? `${trade.durationMinutes} min` : '—'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Macro Bias</p>
              <p className="text-xs font-mono text-slate-300">{trade.dailyMacroBias || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Breakeven</p>
              <p className={`text-xs font-mono ${trade.slMovedToBreakeven ? 'text-green-400' : 'text-slate-600'}`}>
                {trade.slMovedToBreakeven ? 'Sí' : 'No'}
              </p>
            </div>
            {trade.closedAt && (
              <div>
                <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Cerrado</p>
                <p className="text-xs font-mono text-slate-300">{formatTime(trade.closedAt)}</p>
              </div>
            )}
            {trade.exitPrice && (
              <div>
                <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Exit</p>
                <p className="text-xs font-mono text-slate-300">{parseFloat(trade.exitPrice).toFixed(2)}</p>
              </div>
            )}
          </div>
          {trade.traderNotes && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Notas del trader</p>
              <p className="text-xs font-mono text-slate-400 bg-[#0A0A0F] rounded p-2">{trade.traderNotes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function Trades() {
  const [positions,     setPositions]     = useState([]);
  const [trades,        setTrades]        = useState([]);
  const [filter,        setFilter]        = useState('ALL');
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [error,         setError]         = useState(null);
  const [confirmClose,  setConfirmClose]  = useState(null); // position a cerrar
  const [closing,       setClosing]       = useState(false);
  const [lastPoll,      setLastPoll]      = useState(null);
  const pollRef = useRef(null);

  // ── Cargar historial de trades ──────────────────────────────────────
  const loadTrades = useCallback(() => {
    api.getTrades({ limit: 50, status: filter === 'ALL' ? undefined : filter })
      .then(r => setTrades(r.data))
      .catch(() => setError('No se pudieron cargar los trades'))
      .finally(() => setLoadingTrades(false));
  }, [filter]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  // ── Polling de posiciones abiertas cada 5s ──────────────────────────
  const pollPositions = useCallback(() => {
    api.getLivePositions()
      .then(r => {
        setPositions(Array.isArray(r.data) ? r.data : []);
        setLastPoll(new Date());
      })
      .catch(() => {}); // silencioso — no interrumpir UX por fallo de polling
  }, []);

  useEffect(() => {
    pollPositions();
    pollRef.current = setInterval(pollPositions, 5000);
    return () => clearInterval(pollRef.current);
  }, [pollPositions]);

  // ── Cerrar posición ─────────────────────────────────────────────────
  const handleConfirmClose = async () => {
    if (!confirmClose) return;
    setClosing(true);
    try {
      await api.closePosition(confirmClose.id);
      setConfirmClose(null);
      // Refrescar inmediatamente
      setTimeout(pollPositions, 1000);
    } catch {
      setError('Error al cerrar la posición. Revisa los logs.');
    } finally {
      setClosing(false);
    }
  };

  // ── Stats del historial ─────────────────────────────────────────────
  const totalPnl     = trades.reduce((acc, t) => acc + (parseFloat(t.totalPnl ?? t.pnlUsd) || 0), 0);
  const wins         = trades.filter(t => t.result === 'WIN').length;
  const losses       = trades.filter(t => t.result === 'LOSS').length;
  const liveProfit   = positions.reduce((acc, p) => acc + (parseFloat(p.profit) || 0), 0);

  return (
    <div className="p-8 space-y-8">

      {/* Modal confirmación */}
      {confirmClose && (
        <ConfirmCloseModal
          position={confirmClose}
          onConfirm={handleConfirmClose}
          onCancel={() => setConfirmClose(null)}
          closing={closing}
        />
      )}

      {/* ── POSICIONES EN VIVO ─────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-amber-400" />
            <h2 className="font-mono font-bold text-slate-200 text-sm uppercase tracking-wider">
              Posiciones abiertas
            </h2>
            {positions.length > 0 && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-400">
                {positions.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {positions.length > 0 && (
              <div className="text-[11px] font-mono text-slate-500">
                PnL flotante: <PnlValue value={liveProfit} className="text-sm" />
              </div>
            )}
            {lastPoll && (
              <span className="text-[10px] font-mono text-slate-600 flex items-center gap-1">
                <RefreshCw size={9} className="text-slate-700" />
                {lastPoll.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
          </div>
        </div>

        {positions.length === 0 ? (
          <div className="bg-[#13131A] border border-[#1E1E2E] rounded-lg px-4 py-6 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-slate-700" />
            <p className="text-[11px] font-mono text-slate-600">Sin posiciones abiertas</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {positions.map(p => (
              <LivePositionCard
                key={p.id}
                position={p}
                onClose={setConfirmClose}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── HISTORIAL DE TRADES ────────────────────────────────────── */}
      <div className="space-y-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Receipt size={14} className="text-slate-500" />
              <h2 className="font-mono font-bold text-slate-200 text-sm uppercase tracking-wider">
                Historial
              </h2>
            </div>
            <p className="text-[11px] text-slate-600 font-mono mt-0.5 ml-5">
              {trades.length} trades · {wins}W / {losses}L
              {trades.length > 0 && (
                <> · PnL: <PnlValue value={totalPnl} /></>
              )}
            </p>
          </div>

          {/* Filtros */}
          <div className="flex items-center gap-2">
            <Filter size={11} className="text-slate-600" />
            {['ALL', 'OPEN', 'CLOSED', 'PARTIAL'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[10px] font-mono px-2.5 py-1 rounded border transition-all ${
                  filter === f
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                    : 'border-[#1E1E2E] text-slate-600 hover:text-slate-400'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
            <AlertTriangle size={11} /> {error}
          </div>
        )}

        {loadingTrades ? (
          <div className="flex items-center gap-3 text-amber-500 font-mono text-sm py-8">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
            Cargando historial...
          </div>
        ) : trades.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Receipt size={28} className="text-slate-700 mx-auto mb-3" />
              <p className="text-slate-600 font-mono text-sm">Sin trades registrados aún</p>
              <p className="text-slate-700 font-mono text-xs mt-1">
                Los trades paper aparecerán aquí en horario London (8–11am CDMX)
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {trades.map(t => (
              <TradeRow key={t.id} trade={t} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
